import type { TypeRecurring, TypeRecurringCadence } from '@/types/recurring/types.recurring';

const MONTHS = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];

export const CADENCE_MONTHS: Record<TypeRecurringCadence, number> = { monthly: 1, quarterly: 3, semiannual: 6, yearly: 12 };

export const PERIOD_TOKEN = '{periodo}';

const isDay = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

const pad = (value: number): string => String(value).padStart(2, '0');

export const ANCHOR_MIN = 1;
export const ANCHOR_MAX = 31;

export const daysInMonth = (year: number, month: number): number => new Date(Date.UTC(year, month, 0)).getUTCDate();

export const anchorOf = (anchorDay: number): number =>
    Math.min(ANCHOR_MAX, Math.max(ANCHOR_MIN, Number.isInteger(anchorDay) ? anchorDay : ANCHOR_MIN));

export const anchorInMonth = (year: number, month: number, anchorDay: number): number =>
    Math.min(anchorOf(anchorDay), daysInMonth(year, month));

const periodStart = (year: number, month: number, anchorDay: number): string => {
    const shifted = new Date(Date.UTC(year, month - 1, 1));
    const targetYear = shifted.getUTCFullYear();
    const targetMonth = shifted.getUTCMonth() + 1;
    return `${targetYear}-${pad(targetMonth)}-${pad(anchorInMonth(targetYear, targetMonth, anchorDay))}`;
};

export function recurringPeriodLabel(date: string, every: TypeRecurringCadence): string {
    if (!isDay(date)) return '';
    const [year, month] = date.split('-').map(Number);
    if (every === 'monthly') return `${MONTHS[month - 1]} ${year}`;
    if (every === 'quarterly') return `${Math.ceil(month / 3)}° trimestre ${year}`;
    if (every === 'semiannual') return `${Math.ceil(month / 6)}° semestre ${year}`;
    return `anno ${year}`;
}

export function recurringSubjectPreview(subject: string, date: string, every: TypeRecurringCadence): string {
    const label = recurringPeriodLabel(date, every) || recurringPeriodLabel('2026-01-01', every);
    const base = subject.trim();
    return base.includes(PERIOD_TOKEN) ? base.split(PERIOD_TOKEN).join(label) : `${base} · ${label}`;
}

export function recurringFirstDate(startDate: string, anchorDay: number): string {
    if (!isDay(startDate)) return '';
    const [year, month, day] = startDate.split('-').map(Number);
    return day <= anchorInMonth(year, month, anchorDay) ? periodStart(year, month, anchorDay) : periodStart(year, month + 1, anchorDay);
}

export function recurringNextDate(date: string, every: TypeRecurringCadence, anchorDay: number): string {
    if (!isDay(date)) return '';
    const [year, month] = date.split('-').map(Number);
    return periodStart(year, month + (CADENCE_MONTHS[every] ?? 1), anchorDay);
}

export const recurringMonthlyCents = (plan: Pick<TypeRecurring, 'every' | 'template'>): number =>
    Math.round(plan.template.netPayableCents / (CADENCE_MONTHS[plan.every] ?? 1));
